import React, { useEffect, useState } from 'react';
import SEOHead from '../components/SEOHead';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Headphones, Phone, Database, ArrowRightLeft, Cloud, GitBranch, MessageSquare, Workflow, ShieldCheck, ArrowRight, Zap, Users, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Headphones,
  Phone,
  Database,
  ArrowRightLeft,
  Cloud,
  GitBranch,
  MessageSquare,
  Workflow,
  ShieldCheck,
};

const ProductsPreviewPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const findByIds = (ids) => products.filter((p) => ids.includes(p.id));

  const aiDigitalAssistance = findByIds([
    'ai-voice-it-support',
    'ai-customer-care',
    'ai-web-chat',
    'ai-workflow-automation',
    'ai-compliance-studio',
  ]);

  const analyticsProducts = findByIds([
    'analytics-chat-agents',
  ]);

  const discoveryAndMigration = findByIds([
    'enterprise-eda',
    'alteryx-databricks',
    'sql-databricks',
    'sql-fabric',
    'alteryx-fabric',
  ]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
      <SEOHead page="products" />
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-6"
          >
            <h1 className="text-5xl font-bold text-slate-900" data-testid="products-page-heading">
              Enterprise Products & Solutions
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Digian.ai products are organized around two primary pillars: AI Digital Assistance and Data Analytics &
              Visualization. Migration and discovery accelerators are offered as part of consulting-led
              implementations.
            </p>
            <p className="text-sm text-slate-500 max-w-3xl mx-auto">
              Each card below links to a dedicated product page so you can explore capabilities in more detail.
            </p>
          </motion.div>
        </div>
      </section>

      {/* AI Digital Assistance */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">AI Digital Assistance</h2>
              <p className="text-slate-600 max-w-2xl">
                Core voice-first products for IT support, customer care, and internal helpdesks. These are the primary SaaS
                offerings enterprises adopt to "let their data speak for itself".
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {aiDigitalAssistance.map((product, index) => {
              const Icon = iconMap[product.icon] || Headphones;
              return (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`preview-card-${product.id}`}
                >
                  <div className="h-full bg-white rounded-xl border border-slate-200 p-8 hover:border-indigo-500 transition-all duration-300 hover-lift">
                    <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{product.name}</h3>
                    <p className="text-slate-600 leading-relaxed mb-6">{product.description}</p>
                    <div className="grid grid-cols-2 gap-3">
                      <Link
                        to={
                          product.id === 'ai-customer-care' ? '/ai-customer-care' :
                          product.id === 'ai-web-chat' ? '/ai-web-chat' :
                          product.id === 'ai-workflow-automation' ? '/ai-workflow-automation' :
                          product.id === 'ai-compliance-studio' ? '/ai-compliance-studio' :
                          '/ai-voice-agent'
                        }
                        data-testid={`preview-details-${product.id}`}
                      >
                        <Button variant="outline" size="sm" className="w-full">
                          View Details
                        </Button>
                      </Link>
                      <Link to="/demo">
                        <Button size="sm" className="w-full">
                          Get Demo
                        </Button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Implementation accelerators for AI Digital Assistance */}
          {/* <div className="mt-10 rounded-2xl border border-dashed border-indigo-200 bg-indigo-50/40 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Implementation accelerators & consulting options</h3>
            <p className="text-sm text-slate-600 mb-4 max-w-3xl">
              For enterprises modernizing their data platforms alongside AI Digital Assistance, Digian.ai provides
              migration and discovery accelerators as part of end-to-end consulting engagements, not as standalone
              subscriptions.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              {discoveryAndMigration.slice(0, 3).map((accel) => (
                <div key={accel.id} className="bg-white/80 rounded-xl border border-slate-200 p-4 text-sm">
                  <div className="font-semibold text-slate-900 mb-1">{accel.name}</div>
                  <p className="text-slate-600 text-xs leading-relaxed line-clamp-3">{accel.description}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-4">
              These accelerators are used by our solutions team to deliver end-to-end implementations faster and with
              lower risk.
            </p>
          </div> */}
        </div>
      </section>

      {/* Data Analytics & Visualization */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">Data Analytics & Visualization</h2>
              <p className="text-slate-600 max-w-2xl">
                Conversational analytics and chat agents that sit on top of your governed data models, giving business
                users a natural language interface to insight.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {analyticsProducts.map((product, index) => {
              const Icon = iconMap[product.icon] || Database;
              return (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`preview-analytics-${product.id}`}
                >
                  <div className="h-full bg-white rounded-xl border border-slate-200 p-8 hover:border-indigo-500 transition-all duration-300 hover-lift">
                    <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{product.name}</h3>
                    <p className="text-slate-600 leading-relaxed mb-6">{product.description}</p>
                    <div className="grid grid-cols-2 gap-3">
                      <Link to="/analytics-chat-agents">
                        <Button variant="outline" size="sm" className="w-full">
                          View Details
                        </Button>
                      </Link>
                      <Link to="/demo">
                        <Button size="sm" className="w-full">
                          Get Demo
                        </Button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Discovery & migration accelerators framed for analytics */}
          {/* <div className="mt-10 rounded-2xl border border-dashed border-slate-300 bg-white p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Data discovery & migration within analytics programs</h3>
            <p className="text-sm text-slate-600 mb-4 max-w-3xl">
              When enterprises adopt Analytics & Chat Agents, our team can include Enterprise Data Analysis (EDA) and
              platform migration accelerators as part of a consulting engagement to prepare, govern, and modernize the
              underlying data estate.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              {discoveryAndMigration.map((accel) => (
                <div key={accel.id} className="bg-slate-50 rounded-xl border border-slate-200 p-4 text-sm">
                  <div className="font-semibold text-slate-900 mb-1">{accel.name}</div>
                  <p className="text-slate-600 text-xs leading-relaxed line-clamp-3">{accel.description}</p>
                </div>
              ))}
            </div>
          </div> */}
        </div>
      </section>

      {/* Why Digian.ai strip */}
      <section className="py-20 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Why Digian.ai</p>
            <h2 className="text-3xl font-bold text-slate-900">Built differently for enterprise</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: ShieldCheck,
                title: 'Security-First Architecture',
                desc: 'All data encrypted in transit and at rest. Role-based access control, full audit logging, and on-premise deployment options for full data sovereignty.',
              },
              {
                icon: Zap,
                title: 'Fast Time to Value',
                desc: 'Most customers are live in 2–4 weeks. Pre-built connectors, guided onboarding, and a dedicated success engineer get you to first results fast.',
              },
              {
                icon: Users,
                title: 'Designed for Non-Technical Users',
                desc: 'No SQL, no coding, no ML expertise required. Voice queries, chat interfaces, and auto-generated dashboards make data accessible to everyone.',
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-slate-50 rounded-xl border border-slate-200 p-6 flex gap-4"
                >
                  <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Feature checklist */}
          <div className="mt-12 bg-indigo-50 rounded-2xl border border-indigo-100 p-8">
            <h3 className="text-lg font-bold text-slate-900 mb-6 text-center">Every Digian.ai plan includes</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                '14-day free trial, no credit card',
                'Dedicated onboarding engineer',
                'Pre-built enterprise connectors',
                '99.9% uptime SLA',
                'GDPR-aligned data handling',
                'Role-based access control',
                'Real-time analytics dashboard',
                'Priority support (Professional+)',
                'Continuous model improvements',
                // PLACEHOLDER: update list to match your actual plan inclusions
              ].map((f) => (
                <div key={f} className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                  {f}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="text-3xl font-bold text-white">Ready to explore Digian.ai for your enterprise?</h2>
            <p className="text-lg text-slate-300">
              Combine AI Digital Assistance, Analytics & Chat Agents, and our migration and discovery accelerators to
              deliver end-to-end outcomes for your data strategy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo">
                <Button size="lg" variant="secondary">
                  Request a demo
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900">
                  Talk to Digian.ai Sales
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPreviewPage;
